// src/pages/MarketPlace.jsx

import React, { useState, useEffect, useCallback } from "react";
import MarketPlaceLayout from "../../layouts/MarketPlaceLayout";
import ProductList from "../../components/LoggedOut/ProductList";
import { notification } from "antd";
import { getFilteredProducts } from "../../services/productsServices";

export default function MarketPlace() {
  // ————— Favoritos —————
  const [favorites, setFavorites] = useState(() => {
    const stored = localStorage.getItem("productosFavoritos");
    return stored ? JSON.parse(stored) : [];
  });
  const [api, contextHolder] = notification.useNotification();
  useEffect(() => {
    if (favorites.length > 0) {
      api.info({
        message: "Favoritos cargados",
        description: "Se restauraron tus productos favoritos.",
        placement: "topRight",
        duration: 2,
      });
    }
  }, [favorites, api]);

  const handleToggleFavorite = useCallback(
    (product) => {
      // Construye el uniqueId de la misma manera que usas en ProductCard
      const uniqueId = product.FIInventario != null
          ? `${product.FIProducto}-${product.FIInventario}`
          : `prod-${product.FIProducto}`;

      const isAlready = favorites.some((fav) => fav.uniqueId === uniqueId);

      if (isAlready) {
        // Lo quito
        const updated = favorites.filter((fav) => fav.uniqueId !== uniqueId);
        setFavorites(updated);
        localStorage.setItem("productosFavoritos", JSON.stringify(updated));

        api.warning({
          message: "Eliminado de favoritos",
          description: `${
            product.FCMarca?.join(", ") ?? ""
          } ${product.FCNombre} se ha quitado de tus favoritos.`,
          placement: "topRight",
          duration: 2,
        });
      } else {
        // Lo agrego
        const productWithId = { ...product, uniqueId };
        const updated = [...favorites, productWithId];
        setFavorites(updated);
        localStorage.setItem("productosFavoritos", JSON.stringify(updated));

        api.success({
          message: "¡Agregado a favoritos!",
          description: `${
            product.FCMarca?.join(", ") ?? ""
          } ${product.FCNombre} se ha añadido a tus favoritos.`,
          placement: "topRight",
          duration: 2,
        });
      }
    },
    [favorites, api]
  );

  // — Estados de filtros y productos —
  const [searchText, setSearchText] = useState("");
  const [priceRange, setPriceRange] = useState([0, 30000]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // — Función para obtener productos desde la API sin depender de `products` en el closure —
  const fetchProducts = useCallback(
    async (
      {
        filtroSearchText,
        filtroPriceRange,
        filtroSelectedBrands,
        pageNumber = 1,
      },
      isRefreshing = false
    ) => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getFilteredProducts({
          searchText: filtroSearchText,
          minPrice: filtroPriceRange[0],
          maxPrice: filtroPriceRange[1],
          selectedBrands: filtroSelectedBrands,
          page: pageNumber,
        });

        if (Array.isArray(data)) {
          setProducts((prev) => {
            return isRefreshing ? data : [...prev, ...data];
          });
          setHasMore(data.length === 45);
        } else {
          setProducts([]);
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching filtered products:", err);
        setError("Error al cargar los productos filtrados");
        setProducts([]);
        setHasMore(false);
      } finally {
        setIsLoading(false);
      }
    },
    [] // NO incluir `products` aquí
  );

  // ————— Al montar, cargar la primera página sin filtros —————
  useEffect(() => {
    fetchProducts(
      {
        filtroSearchText: "",
        filtroPriceRange: [0, 30000],
        filtroSelectedBrands: [],
        pageNumber: 1,
      },
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ————— Aplicar filtros: recibir valores nuevos y llamar a fetch inmediatamente —————
  const handleFilterSubmit = ({
    searchText: newSearch,
    priceRange: newRange,
    selectedBrands: newBrands,
  }) => {
    // 1) Actualizo estados de filtro (para UI o siguientes páginas)
    setSearchText(newSearch);
    setPriceRange(newRange);
    setSelectedBrands(newBrands);

    // 2) Reinicio paginación y limpio lista
    setPage(1);
    setHasMore(true);
    setProducts([]);

    // 3) Traigo página 1 con estos nuevos filtros
    fetchProducts(
      {
        filtroSearchText: newSearch,
        filtroPriceRange: newRange,
        filtroSelectedBrands: newBrands,
        pageNumber: 1,
      },
      true
    );
  };

  // ————— Cargar más páginas al hacer “Mostrar más” —————
  const loadMore = () => {
    if (!hasMore || isLoading) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(
      {
        filtroSearchText: searchText,
        filtroPriceRange: priceRange,
        filtroSelectedBrands: selectedBrands,
        pageNumber: nextPage,
      },
      false
    );
  };

  // ————— Limpiar todos los filtros y recargar la página 1 —————
  const handleClearAllFilters = () => {
    setSearchText("");
    setPriceRange([0, 30000]);
    setSelectedBrands([]);
    setPage(1);
    setHasMore(true);
    setProducts([]);
    fetchProducts(
      {
        filtroSearchText: "",
        filtroPriceRange: [0, 30000],
        filtroSelectedBrands: [],
        pageNumber: 1,
      },
      true
    );
  };

  return (
    <>
      {contextHolder}
      <MarketPlaceLayout
        favorites={favorites}
        onRemoveFavorite={handleToggleFavorite}
        onFilterSubmit={handleFilterSubmit}
        onClearFilters={handleClearAllFilters}
      >
        <ProductList
          products={products}
          isLoading={isLoading}
          error={error}
          hasMore={hasMore}
          loadMore={loadMore}
          favorites={favorites}
          handleAddToFavorites={handleToggleFavorite}
        />
      </MarketPlaceLayout>
    </>
  );
}
